import env from 'config';
import { MachineContext } from 'contexts/machine.context';
import { IntercomEvents } from 'enums/intercom.enums';
import useLatest from 'hooks/useLatestRef';
import { useContext, useEffect, useMemo } from 'react';
import { useIntercom } from 'react-use-intercom';
import { paths } from 'routes/paths';
import { useCookiesStore } from 'stores/use-cookies-store';
import useMachineStatusStore from 'stores/use-machine-status-store';

// weirdness because it must live within the intercom provider
export const IntercomListener = () => {
  const snapshot = useCookiesStore((s) => s.snapshot);

  const { isOpen, showArticle, showNewMessage, update } = useIntercom();

  const { lastMS, machine } = useContext(MachineContext);

  const latestSnapshot = useLatest(snapshot);
  const latestLastMs = useLatest(lastMS);

  // update snapshot URL when opening the client
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    useMachineStatusStore
      .getState()
      .generateIntercomUrl({
        pitchID: latestSnapshot.current.pitch_id,
        snapshot: latestSnapshot.current,
        lastMS: latestLastMs.current,
      })
      .then((url) => {
        if (!url) {
          // either nothing changed or something failed, so keep the existing URL (if any) intact
          return;
        }

        update({ customAttributes: { 'Machine Snapshots URL': url } });
      });
  }, [isOpen]);

  // allow opening specific articles from elsewhere in the app
  useEffect(() => {
    document.addEventListener(IntercomEvents.ShowArticle, (message) => {
      const data = (message as CustomEvent).detail as { id: number };
      showArticle(data.id);
    });
  }, []);

  // allow opening chat from elsewhere in the app
  useEffect(() => {
    document.addEventListener(IntercomEvents.ShowNewMessage, () => {
      showNewMessage();
    });
  }, []);

  const statusUrl = useMemo(() => {
    const params = new URLSearchParams({
      machineID: machine.machineID,
    });

    return `${env.self_url}${paths.admin.machines}?${params.toString()}`;
  }, [env.self_url, machine.machineID]);

  useEffect(() => {
    update({ customAttributes: { 'Machine Status': statusUrl } });
  }, [statusUrl]);

  return null;
};
